import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "max-w-[920px]" }
const _hoisted_2 = { class: "md:flex" }
const _hoisted_3 = { id: "fuel-stations-results" }
const _hoisted_4 = {
  key: 0,
  class: "md:flex grid gap-2 py-2"
}
const _hoisted_5 = {
  key: 0,
  class: "flex items-center text-nowrap"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "flex pt-2 pb-1 md:pt-3 md:pb-2 md:my-2 items-center" }
const _hoisted_8 = { class: "mr-5 py-3 md:inline-block hidden" }
const _hoisted_9 = { class: "h-8 w-8 md:h-12 md:w-12 rounded content-center" }
const _hoisted_10 = ["src", "alt"]
const _hoisted_11 = { class: "py-3" }
const _hoisted_12 = { class: "mb-2 md:flex grid gap-2 md:gap-4 items-center" }
const _hoisted_13 = { class: "flex items-center" }
const _hoisted_14 = { class: "mr-2 py-3 md:hidden" }
const _hoisted_15 = { class: "h-8 w-8 md:h-12 md:w-12 rounded content-center" }
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = { class: "text-base-bold md:text-xl-bold text-racq-dark-grey mr-3 md:mr-0 line-clamp-2" }
const _hoisted_18 = { class: "flex gap-1 h-auto" }
const _hoisted_19 = { class: "flex" }
const _hoisted_20 = { class: "text-xs md:text-m text-racq-med-grey line-clamp-1" }
const _hoisted_21 = { class: "ml-auto w-[124px] md:w-[152px]" }
const _hoisted_22 = { class: "text-xl2-bold md:text-xl3-bold w-[124px] md:w-[152px] h-[78px] md:h-[60px] content-center text-center text-racq-dark-grey bg-racq-neutral-grey rounded" }
const _hoisted_23 = { class: "mt-1 text-xs text-racq-med-grey text-center" }
const _hoisted_24 = { key: 2 }

import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import GoogleMapAddressInput from '@/components/GoogleMap/GoogleMapAddressInput.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import Tag from '@/components/Tag/Tag.vue';
import SearchResultsList from '@/components/SearchResultsList/SearchResultsList.vue';
import { convertSitecoreFormFieldToVueFormField } from '@/composables/useFormInputData';
import { TagBgColour, TagBorderColour, TagSize, TagTypes } from '@/enums/tag-enums';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { useGoogleMapStore } from '@/store';
import { useFuelFinderStore } from '@/store/fuel-finder/fuel-finder.state';
import { format, formatDistanceToNow } from 'date-fns';
import { computed, onMounted, ref } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { PriceIndicatorType, TrendIndicatorType } from '@/enums/fuel-price-trend.enum';
import { PageAnalyticsBuilder, PageEvents, SiteSection } from '@/utils/page-analytics-builder';
import { useRoute } from 'vue-router';

const itemsPerPage = 10;

export default /*@__PURE__*/_defineComponent({
  __name: 'FuelFinder',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const props = __props;
const searchResultsListContent = props.content.find((item) => item.name == 'searchResultsList') as any;
const fairFuelTooltip = props.content.find((item) => item.name == 'fairFuelTooltip') as any;
const suburbLookup = props.content.find((item) => item.name == 'suburbLookup') as any;

const googleMapStore = useGoogleMapStore();
googleMapStore.setSuburbPostcodeMode(true);
const fuelFinderStore = useFuelFinderStore();
const route = useRoute();

const formData = ref<Record<string, string>>({});
const loading = ref(false);
const page = ref(1);
const showInitialBanner = ref(true);
const isTooltipOpen = ref(false);

const fuelTypeBindings = computed(() => {
  const input = props.content.find((input) => input.name == 'fuelType');
  return convertSitecoreFormFieldToVueFormField(input!, 'fuelType');
});

const fuelStations = computed(() => fuelFinderStore.fuelStations);
const lowestPrice = computed(() => fuelFinderStore.lowestPrice);
const averagePrice = computed(() => fuelFinderStore.averagePrice);
const priceState = computed(() => fuelFinderStore.priceState);
const priceTrend = computed(() => fuelFinderStore.priceTrend);
const lastUpdatedAt = computed(() => format(fuelFinderStore.lastUpdated ?? new Date(), 'dd MMM HH:mm a'));
const paginatedStations = computed(() =>
  fuelStations.value.slice((page.value - 1) * itemsPerPage, page.value * itemsPerPage)
);

// -----------------------------------
// Compute labels
// -----------------------------------
const computedLastUpdatedAt = (lastUpdatedAt: string, dataSource: string) =>
  computed(() => {
    const dataSourceSymbol = dataSource?.includes('OPIS') ? '^' : '*';
    return replaceBookmark(
      'lastUpdated',
      (props.content.find((item) => item.name == 'priceChangeLabel')?.label?.value ?? '') + dataSourceSymbol,
      fromNow(lastUpdatedAt)
    );
  });

const fairPriceLinkBreakLabel = computed(() => {
  return replaceBookmark(
    'fairFuelPrice',
    props.content.find((item) => item.name == 'fairFuelLineBreakLabel')?.label?.value ?? '',
    fuelFinderStore.averagePrice?.toFixed(2)
  );
});

const initialSearchBanner = computed(() => props.content.find((item) => item.name == 'initialSearchBanner')?.label);
const cheapestLabel = computed(() => props.content.find((item) => item.name == 'cheapestLabel')?.label?.value);
const discountsLabel = computed(() => props.content.find((item) => item.name == 'discountLabel')?.label?.value);
const heading = computed(() => props.content.find((item) => item.name == 'heading')?.label);
const resultNotFound = computed(() => props.content.find((item) => item.name == 'resultNotFound')?.label);
const priceHigh = computed(() => props.content.find((item) => item.name == 'priceHigh')?.label);
const priceLow = computed(() => props.content.find((item) => item.name == 'priceLow')?.label);
const priceGoingUp = computed(() => props.content.find((item) => item.name == 'priceGoingUp')?.label);
const priceGoingDown = computed(() => props.content.find((item) => item.name == 'priceGoingDown')?.label);
const copyright = computed(() => props.content.find((item) => item.name == 'copyright')?.label);
const copyrightWithResults = computed(() => props.content.find((item) => item.name == 'copyrightWithResults')?.label);

onMounted(async () => {
  await googleMapStore.getSCGoogleMapsSettings();
  if (googleMapStore.googleMapSettingsItem) {
    googleMapStore.setApiKey(googleMapStore.googleMapSettingsItem.apiKey);
    await googleMapStore.loadGoogleMapsApi();
  }
  pushAnalytics();
});

const onSubmit = async () => {
  loading.value = true;
  showInitialBanner.value = false;
  await fuelFinderStore
    .findFuel(googleMapStore.latitude, googleMapStore.longitude, formData.value.fuelType)
    .finally(() => {
      page.value = 1;
      loading.value = false;
    });

  pushAnalytics(true);
};

const fromNow = (timestring: string) => {
  return formatDistanceToNow(new Date(timestring));
};

const showPriceAlert = (index: number) => {
  if (Number.isNaN(averagePrice.value) || !Number(averagePrice.value)) return false;
  const totalIndex = index + (page.value - 1) * itemsPerPage;
  const currentStation = fuelStations?.value[totalIndex];
  const previousStation = totalIndex ? fuelStations?.value[totalIndex - 1] : null;

  if (!previousStation && currentStation?.price > averagePrice.value) return true; // edge case if lowest price is higher than fair price

  return previousStation && previousStation.price <= averagePrice.value && currentStation?.price > averagePrice.value;
};

const updatePage = (p: number) => {
  page.value = p;
  scrollToTop();
};

const scrollToTop = () => {
  const iframe = window.parent.document.querySelector(
    (route.query.iframeSelector as string) ?? 'iframe'
  ) as HTMLIFrameElement;
  iframe?.scrollIntoView({
    block: 'start',
    inline: 'start',
    behavior: 'smooth',
  });
};

const pushAnalytics = async (searchInfo?: boolean) => {
  // Push analytics
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.pageAnalytics.webPageDetails = {
    name: 'Fuel prices',
    siteSection: 'RACQ - Car',
  };
  pageAnalytics.pageAnalytics.form = {
    authStatus: 'Not applicable',
  };
  delete pageAnalytics.pageAnalytics.profileInfo;
  pageAnalytics.pageAnalytics.event = `${SiteSection.RACQ}.${PageEvents.PAGE_VIEW}`;

  // extra info for search
  if (searchInfo) {
    pageAnalytics.pageAnalytics.searchInfo = {
      onsiteSearchFilter: formData.value.fuelType,
      onsiteSearchTerm: googleMapStore.formattedAddress,
      onsiteSearchType: 'Fuel prices',
    };
    const onsiteSearchResults = [];
    if ([PriceIndicatorType.High, PriceIndicatorType.Low].includes(priceState.value!)) {
      onsiteSearchResults.push(
        priceState.value == PriceIndicatorType.High ? priceHigh.value?.value : priceLow.value?.value
      );
    }
    if (priceTrend.value) {
      onsiteSearchResults.push(
        TrendIndicatorType.Up == priceTrend.value ? priceGoingUp.value?.value : priceGoingDown.value?.value
      );
    }

    pageAnalytics.pageAnalytics.searchInfo.onsiteSearchResults = onsiteSearchResults
      .join(' - ')
      .replace(/<[^>]*>/g, '')
      .replaceAll('\n', '');
    pageAnalytics.pageAnalytics.event = `${SiteSection.RACQ}.${PageEvents.PAGE_SEARCH}`;
  }
  pageAnalytics.pushAnalytics();
};

const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
  if (isTooltipOpen.value) {
    setTimeout(() => {
      document.querySelector('.v-popper__inner')?.addEventListener('click', () => {
        isTooltipOpen.value = false;
      });
    }, 500);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createBlock(FullScreenSpinner, { key: 0 }))
      : _createCommentVNode("", true),
    (heading.value?.value)
      ? (_openBlock(), _createBlock(_unref(RichText), {
          key: 1,
          field: heading.value,
          class: "text-xl-bold mb-5"
        }, null, 8, ["field"]))
      : _createCommentVNode("", true),
    _createVNode(_component_FormKit, {
      modelValue: formData.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData).value = $event)),
      type: "form",
      onSubmit: onSubmit
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(RACQFormKit, {
            bindings: {
            ...fuelTypeBindings.value,
            deselect: false,
          }
          }, null, 8, ["bindings"]),
          _createVNode(GoogleMapAddressInput, {
            label: _unref(suburbLookup)?.label?.value,
            outerClass: _unref(suburbLookup)?.outerClass?.value,
            innerClass: _unref(suburbLookup)?.innerClass?.value,
            wrapperClass: "",
            placeholder: _unref(suburbLookup)?.placeholder?.value,
            requiredValidationMessage: _unref(suburbLookup)?.isRequiredErrorMessage?.value
          }, null, 8, ["label", "outerClass", "innerClass", "placeholder", "requiredValidationMessage"]),
          _createVNode(_component_FormKit, {
            type: "submit",
            label: "Search",
            inputClass: "btn btn-md btn-primary mt-7",
            outerClass: "md:ml-4"
          })
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _cache[3] || (_cache[3] = _createElementVNode("hr", { class: "my-5 md:my-6" }, null, -1)),
    (showInitialBanner.value)
      ? (_openBlock(), _createBlock(_unref(RichText), {
          key: 2,
          field: initialSearchBanner.value,
          class: "p-8 pr-12 grid md:flex gap-4 items-center bg-racq-anti-flash-white rounded"
        }, null, 8, ["field"]))
      : (!fuelStations.value.length)
        ? (_openBlock(), _createBlock(_unref(RichText), {
            key: 3,
            field: resultNotFound.value,
            class: "p-8 pr-12 gap-1 grid bg-racq-info-light-red rounded"
          }, null, 8, ["field"]))
        : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      (_unref(searchResultsListContent))
        ? (_openBlock(), _createBlock(SearchResultsList, {
            key: 0,
            content: _unref(searchResultsListContent),
            items: fuelStations.value,
            itemsPerPage: itemsPerPage,
            defaultPage: page.value,
            lastUpdatedAt: lastUpdatedAt.value,
            scrollTarget: "#fuel-stations-results",
            onPageChanged: updatePage
          }, {
            subHeader: _withCtx(() => [
              (fuelStations.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    (priceState.value == _unref(PriceIndicatorType).High)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 0,
                          field: priceHigh.value,
                          class: "px-6 py-5 gap-4 flex bg-racq-info-light-red rounded w-full"
                        }, null, 8, ["field"]))
                      : (priceState.value == _unref(PriceIndicatorType).Low)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 1,
                            field: priceLow.value,
                            class: "px-6 py-5 gap-4 flex bg-racq-info-light-green rounded w-full"
                          }, null, 8, ["field"]))
                        : _createCommentVNode("", true),
                    (priceTrend.value == _unref(TrendIndicatorType).Up)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 2,
                          field: priceGoingUp.value,
                          class: "px-6 py-5 gap-4 flex bg-racq-anti-flash-white rounded w-full"
                        }, null, 8, ["field"]))
                      : (priceTrend.value == _unref(TrendIndicatorType).Down)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 3,
                            field: priceGoingDown.value,
                            class: "px-6 py-5 gap-4 flex bg-racq-anti-flash-white rounded w-full"
                          }, null, 8, ["field"]))
                        : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]),
            item: _withCtx(({ item: station, index }) => [
              (showPriceAlert(index))
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _withDirectives(_createVNode(Tag, {
                      outerClass: "pr-8",
                      tagType: _unref(TagTypes).noIcon,
                      tagText: fairPriceLinkBreakLabel.value,
                      tagSize: _unref(TagSize).linkBreak,
                      tagBgColour: _unref(TagBgColour).error,
                      onClick: toggleTooltip
                    }, null, 8, ["tagType", "tagText", "tagSize", "tagBgColour"]), [
                      [
                        _directive_tooltip,
                        {
                content: _unref(fairFuelTooltip)?.label?.value,
                triggers: [],
                shown: isTooltipOpen.value,
              },
                        void 0,
                        { bottom: true }
                      ]
                    ]),
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "border-b-2 w-full border-dashed !border-racq-error-red" }, null, -1))
                  ]))
                : index
                  ? (_openBlock(), _createElementBlock("hr", _hoisted_6))
                  : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("img", {
                      class: "mx-auto max-h-full max-w-full",
                      src: station.brandLogoSrc,
                      alt: station.brand
                    }, null, 8, _hoisted_10)
                  ])
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          _createElementVNode("img", {
                            class: "mx-auto max-h-full max-w-full",
                            src: station.brandLogoSrc,
                            alt: station.brand
                          }, null, 8, _hoisted_16)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_17, _toDisplayString(station.name), 1)
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      (station.discount && discountsLabel.value)
                        ? (_openBlock(), _createBlock(Tag, {
                            key: 0,
                            tagType: _unref(TagTypes).discount,
                            tagText: discountsLabel.value,
                            tagSize: _unref(TagSize).sm,
                            tagBgColour: _unref(TagBgColour).infoBlue
                          }, null, 8, ["tagType", "tagText", "tagSize", "tagBgColour"]))
                        : _createCommentVNode("", true),
                      (lowestPrice.value == station.price && cheapestLabel.value)
                        ? (_openBlock(), _createBlock(Tag, {
                            key: 1,
                            tagType: _unref(TagTypes).noIcon,
                            tagText: cheapestLabel.value,
                            tagSize: _unref(TagSize).sm,
                            tagBgColour: _unref(TagBgColour).antiFlashWhite,
                            tagBorderColour: _unref(TagBorderColour).lightblue
                          }, null, 8, ["tagType", "tagText", "tagSize", "tagBgColour", "tagBorderColour"]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "align-text-top inline-block location-icon bg-center bg-no-repeat w-4 h-4 md:w-[18px] mdh-[18px] mr-1" }, null, -1)),
                    _createElementVNode("p", _hoisted_20, _toDisplayString(station.address) + ", " + _toDisplayString(station.suburb), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("div", _hoisted_22, _toDisplayString(station.price.toFixed(1)), 1),
                  _createElementVNode("div", _hoisted_23, [
                    _createVNode(_unref(RichText), {
                      field: computedLastUpdatedAt(station.lastUpdated, station.dataSourceName)
                    }, null, 8, ["field"])
                  ])
                ])
              ]),
              (index + 1 == paginatedStations.value.length)
                ? (_openBlock(), _createElementBlock("hr", _hoisted_24))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["content", "items", "defaultPage", "lastUpdatedAt"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(RichText), {
        field: fuelStations.value?.length ? copyrightWithResults.value : copyright.value
      }, null, 8, ["field"])
    ])
  ]))
}
}

})